import { Link } from "react-router-dom";
import { FloopLogo } from "@/components/FloopLogo";
import { Twitter, Instagram, Youtube, Linkedin, Github, Mail, Phone, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const footerLinks = {
  product: {
    title: "Produit",
    links: [
      { label: "Fonctionnalités", href: "/features" },
      { label: "Tarifs", href: "/pricing" },
      { label: "Galerie", href: "/gallery" },
      { label: "API", href: "/api" },
      { label: "Changelog", href: "/changelog" },
    ],
  },
  resources: {
    title: "Ressources",
    links: [
      { label: "Documentation", href: "/documentation" },
      { label: "Tutoriels", href: "/tutorials" },
      { label: "Blog", href: "/blog" },
      { label: "Centre d'aide", href: "/help" },
      { label: "Communauté", href: "/community" },
    ],
  },
  company: {
    title: "Entreprise",
    links: [
      { label: "À propos", href: "/about" },
      { label: "Carrières", href: "/careers" },
      { label: "Presse", href: "/press" },
      { label: "Contact", href: "/contact" },
      { label: "Partenaires", href: "/partners" },
    ],
  },
  legal: {
    title: "Légal",
    links: [
      { label: "Confidentialité", href: "/privacy" },
      { label: "CGU", href: "/terms" },
      { label: "Cookies", href: "/cookies" },
      { label: "RGPD", href: "/rgpd" },
    ],
  },
};

const socialLinks = [
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Youtube, href: "#", label: "YouTube" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Github, href: "#", label: "GitHub" },
];

export const PremiumFooter = () => {
  const [email, setEmail] = useState("");

  return (
    <footer className="relative overflow-hidden">
      {/* Mesh gradient background */}
      <div className="absolute inset-0 mesh-gradient-bg opacity-30" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      {/* CTA Section */}
      <div className="relative z-10 py-20 border-b border-white/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 font-display">
              Prêt à créer vos{" "}
              <span className="gradient-text-animated">premiers visuels IA</span> ?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Rejoignez +2 800 créateurs qui économisent des heures chaque semaine grâce à Floop. Commencez gratuitement.
            </p>
            <Link to="/create">
              <Button className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-10 py-6 text-lg group shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40 transition-all duration-300 hover:scale-105">
                <Sparkles className="w-5 h-5 mr-2" />
                Créer ma première image
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="relative z-10 py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-12 mb-12">
            {/* Brand column */}
            <div className="lg:col-span-2">
              <Link to="/" className="inline-flex items-center gap-2 mb-6 group">
                <FloopLogo size="md" />
                <span className="text-2xl font-bold bg-gradient-floop bg-clip-text text-transparent font-display group-hover:opacity-80 transition-opacity">
                  Floop
                </span>
              </Link>
              <p className="text-muted-foreground mb-6 max-w-sm leading-relaxed">
                Le générateur d'images IA qui transforme vos idées en visuels professionnels. Simple, rapide, accessible.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <a 
                  href="mailto:contact@floop.sbs" 
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Mail className="w-4 h-4" />
                  </div>
                  contact@floop.sbs
                </a>
                <a 
                  href="https://wa.me/237697747365?text=Bonjour%20Floop%20!%20J%27ai%20une%20question."
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-green-500 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  +237 697 747 365
                </a>
                <p className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-base">
                    📍
                  </span>
                  Bertoua, Cameroun
                </p>
              </div>
              
              {/* Social links */}
              <div className="flex items-center gap-2">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 rounded-xl glass-card border border-white/10 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 hover:scale-110 transition-all duration-300"
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Links columns */}
            {Object.entries(footerLinks).map(([key, section]) => (
              <div key={key}>
                <p className="font-semibold text-foreground mb-5">{section.title}</p>
                <ul className="space-y-3">
                  {section.links.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.href}
                        className="text-muted-foreground hover:text-primary transition-colors text-sm inline-flex items-center gap-1 group"
                      >
                        <span className="relative">
                          {link.label}
                          <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-primary transition-all duration-300 group-hover:w-full" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter */}
          <div className="glass-card border border-white/10 rounded-2xl p-6 md:p-8 mb-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="font-semibold text-foreground mb-1">Newsletter Floop</p>
                <p className="text-sm text-muted-foreground">Recevez nos dernières actualités et conseils créatifs.</p>
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="flex-1 md:w-64 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                />
                <Button className="bg-gradient-floop hover:opacity-90 text-white rounded-xl px-6">
                  S'inscrire
                </Button>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Floop. Tous droits réservés.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                Fait avec 
                <span className="text-primary animate-pulse">💜</span> 
                pour les créatifs
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
